import { signal, effect, inject, ElementRef, DestroyRef, AfterViewInit, Component, OnDestroy, input, Signal, Injector, isSignal } from '@angular/core';
import { debounced } from './debounced';

export class AutosizingObserver{
  constructor(
    readonly enabled: Signal<boolean> = signal(true),
    readonly onResize: () => void,
    readonly elementToObserve: ElementRef<HTMLElement>,
    readonly injector: Injector,
    readonly autosizingObserverOptions?: AutosizingObserverOptions
  ) {
    let [debounceTime, maxWaitTime] = [
      this.autosizingObserverOptions?.resizeDebounceTime,
      this.autosizingObserverOptions?.resizeMaxWaitTime
    ];

    debounceTime ??= 150;
    maxWaitTime ??= 300;

    this.resizeDebounceTime = isSignal(debounceTime) ? debounceTime : signal(debounceTime);
    this.resizeMaxWaitTime = isSignal(maxWaitTime) ? maxWaitTime : signal(maxWaitTime);
    this.destroyRef = this.autosizingObserverOptions?.destroyRef;
  }

  readonly resizeDebounceTime: Signal<number> = signal(150);
  readonly resizeMaxWaitTime: Signal<number> = signal(300);
  readonly destroyRef: DestroyRef;

  init() {
      this.checkAutoSize();
  }

  private resizeToContainerDebounced: Function & { cancel: Function; };

  private readonly updateResizeToContainerDebounced = effect((onCleanup) => {
    this.resizeToContainerDebounced = debounced(
      () => {
        this.onResize();
      },
      this.resizeDebounceTime(),
      { destroyRef: this.destroyRef, maxWait: this.resizeMaxWaitTime() });

    onCleanup(() => this.cancelResizeToContainerDebounced());
  }, {injector: this.injector});

  private cancelResizeToContainerDebounced() {
    try {
      this.resizeToContainerDebounced?.cancel();
    }
    finally {
      this.resizeToContainerDebounced = null;
    }
  }

  private readonly autoSizeChanged = effect(() => this.checkAutoSize(), {injector: this.injector});

  private autoSizeStarted = false;
  private resizeObserver: ResizeObserver;
  private windowResizeCallback: any;

  private checkAutoSize() {
    if (this.enabled())
      this.startAutoSize();
    else
      this.stopAutoSize();
  }

  private startAutoSize() {
    if (this.autoSizeStarted)
      return;

    this.autoSizeStarted = true;

    try {
      this.windowResizeCallback = () => this.resizeToContainerDebounced();
      window.addEventListener('resize', this.windowResizeCallback);
    }
    finally {
      try {
        this.resizeObserver = new ResizeObserver(() => this.resizeToContainerDebounced());
        this.resizeObserver.observe(this.elementToObserve.nativeElement, { box: 'border-box' });
      }
      finally {
        this.onResize();
      }
    }
  }

  private stopAutoSize() {
    if (!this.autoSizeStarted)
      return;

    this.autoSizeStarted = false;

    try {
      this.resizeObserver?.disconnect();
    }
    finally {
      this.resizeObserver = null;

      try {
        if (this.windowResizeCallback)
          window.removeEventListener('resize', this.windowResizeCallback);
      }
      finally {
        this.windowResizeCallback = null;
      }
    }
  }

  destroy() {
    this.cancelResizeToContainerDebounced();
    this.stopAutoSize();
  }
};

export interface AutosizingObserverOptions {
   resizeDebounceTime?: Signal<number> | number,
   resizeMaxWaitTime?: Signal<number> | number,
   destroyRef: DestroyRef,
}
